import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { fmtUSD, daysSince } from "../../utils/reports/format";
import { isDownRoom, roomCost } from "../../utils/reports/roomCost";

/**
 * Room Downtime Aging — every Down/Facilities room with days down and the
 * estimated item cost to bring it back up, sorted longest-down first.
 */
export default function GenerateRoomDowntimeAging({ user }) {
  const { rooms, items } = useContext(PropertyContext);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const downRooms = (rooms || [])
        .filter(isDownRoom)
        .map((room) => ({
          name: room.displayName || room.id,
          status: room.status,
          days: daysSince(room.lastDateDown),
          housekeeping: room.selectedHousekeepingRequirements || "—",
          maintenance: room.selectedMaintenanceRequirements || "—",
          cost: roomCost(room, items),
        }))
        .sort((a, b) => (b.days ?? -1) - (a.days ?? -1));

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Room Downtime Aging",
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const grandTotal = downRooms.reduce((s, r) => s + r.cost, 0);

      const body = downRooms.map((r) => [
        r.name,
        r.status,
        r.days == null ? "—" : String(r.days),
        r.housekeeping,
        r.maintenance,
        fmtUSD(r.cost),
      ]);

      body.push([
        { content: `${downRooms.length} room(s)`, colSpan: 5, styles: { fontStyle: "bold" } },
        { content: fmtUSD(grandTotal), styles: { fontStyle: "bold", halign: "right" } },
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Room", "Status", "Days Down", "Housekeeping", "Maintenance", "Est. Cost"]],
        body,
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          2: { halign: "right" },
          5: { halign: "right" },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Room_Downtime_Aging", { ext: "pdf" })
      );
    } catch (e) {
      console.error("Error generating Room Downtime Aging:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "Room Downtime Aging"}
    </Button>
  );
}
